import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { CurrencyModel } from '@root/entities/currency'
import { PriceFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'

import type { AggregatedPositionModel } from '@root/entities/position'
import type BigNumber from 'bignumber.js'

type Props = {
  position: AggregatedPositionModel
}

export const TradePositionDetailsHeader: React.FC<Props> = ({ position }) => {
  const { t } = useTranslation()

  const [entryPriceUsd] = useAtom(position.entryPriceUsd)
  const [entryPriceNative] = useAtom(position.entryPriceNative)

  const targetPriceUsd = useOptionalAtom(position.targetPriceUsd)
  const targetPriceNative = useOptionalAtom(position.targetPriceNative)

  const closePriceUsd = useOptionalAtom(position.closePriceUsd)
  const closePriceNative = useOptionalAtom(position.closePriceNative)

  const nativeCurrency = CurrencyModel.nativeOf(position.network)

  const renderPrice = (price: BigNumber | undefined) => {
    if (!price) {
      return '-'
    }

    return (
      <>
        <PriceFormatted price={price} currency={nativeCurrency.ticker} />
      </>
    )
  }
  const renderPriceUsd = (priceUsd: BigNumber | undefined) => {
    if (!priceUsd) {
      return '-'
    }

    return <PriceFormatted price={priceUsd} currency="$" />
  }

  return (
    <Root>
      <Groups>
        <Group>
          <Label>{t('trade.entry-price')}</Label>
          <Price>{renderPrice(entryPriceNative)}</Price>
          <PriceUsd>{renderPriceUsd(entryPriceUsd)}</PriceUsd>
        </Group>
        <GroupSeparator />
        <Group>
          <Label>{t('trade.target-price')}</Label>
          <Price>{renderPrice(targetPriceNative)}</Price>
          <PriceUsd>{renderPriceUsd(targetPriceUsd)}</PriceUsd>
        </Group>
        <GroupSeparator />
        <Group>
          <Label>{t('trade.trigger-price')}</Label>
          <Price>{renderPrice(closePriceNative)}</Price>
          <PriceUsd>{renderPriceUsd(closePriceUsd)}</PriceUsd>
        </Group>
      </Groups>
      <HeadingUnderline />
    </Root>
  )
}

const Root = styled.header``
const Groups = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 60px;
`

const GroupSeparator = styled.span`
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--backgroundA-4) 100%
  );

  width: 1px;
  margin-inline: 20px;
`

const HeadingUnderline = styled.span`
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--backgroundA-4) 50%,
    transparent 100%
  );

  display: flex;
  height: 1px;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const Label = styled.span`
  color: var(--text-11);
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
`
const Price = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
`

const PriceUsd = styled.span`
  color: var(--text-7);
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
`
